<template>
<div class="vuetify-wrapper">
    <div class="filter-container v-application">
        <h4>Filter </h4>
        <div class="v-application ml-10">
            <v-text-field
                v-model="filter.title"
                placeholder="Titel"
                dense
                outlined
                hide-details
                clearable
                @click:clear="filter.title=''"
                >
                <template slot="prepend-inner">
                    <v-icon color="info"  @click="onFilterChanged" v-if="filter.filter_type==='contains'">mdi-equal</v-icon>
                    <v-icon color="info"  @click="onFilterChanged" v-else>mdi-not-equal</v-icon>
                </template>
            </v-text-field>
            <v-select
                placeholder="Zustand"
                v-model="filter.wf_state"
                :items="statusOptions"
                class="ml-2"
                hide-details
                outlined
                dense
                clearable
            ></v-select>
            <v-btn
                @click="clearFilter"
                class="ml-5 mt-1"
                small
                rounded
                :disabled="!isFiltered"
            >
                <v-icon class="ml-1" small>mdi-cancel</v-icon>
                Filter zurücksetzen
            </v-btn>
        </div>
    </div>
    <v-card :disabled="isLoading" class="v-application">
        <v-card-text class="pb-0">
            <v-data-table
                :headers="headers"
                :items="ticketsList"
                :items-per-page="10"
                :page.sync="pageNumber"
                :options.sync="tableOptions"
                :server-items-length="ticketsCount"
                item-key="_id"
                :loading="isLoading"
                :expanded.sync="expanded"
                @update:options="onOptionsChange"
                class="v-application--is-ltr"
                no-data-text="Keine Suchergebnisse"
                loading-text="Wird geladen..."
                disable-sort
                :footer-props="{'items-per-page-options':[5, 10, 25, 50], 'show-first-last-page': true, 'show-current-page': true, 'items-per-page-text': 'Zeilen pro Seite'}"
            >
                <template v-slot:item="{ item, expand, isExpanded }">
                    <tr :data-id="item?._id">
                        <td>
                            {{item?._id}}
                        </td>
                        <td>
                            {{workflow_states[item.wf_state]}}
                        </td>
                        <td class="col-md-3">
                            <div class="d-flex flex-column">
                                <a :href="`/pharmacies/${item?.ref_pharmacy?._id}`" target="_blank">
                                    {{item?.ref_pharmacy?.name || ""}}
                                </a>
                                <a :href="`/pharmacists/${item?.ref_pharmacist?._id}`" target="_blank">
                                    {{item?.ref_pharmacist?.firstname || ""}} {{item?.ref_pharmacist?.lastname || ""}}
                                </a>
                                <a
                                    v-if="ticketAppointmentHref(item)"
                                    :href="ticketAppointmentHref(item)"
                                    target="_blank"
                                >
                                    {{formatAppointmentDate(item?.ref_appointment?.date)}}
                                </a>
                                <span v-else>
                                    {{formatAppointmentDate(item?.ref_appointment?.date)}}
                                </span>
                            </div>
                        </td>
                        <td class="col-md-5">
                            {{item.title}}
                        </td>
                        <td class="col-md-1">
                            <v-btn icon @click="expand(!isExpanded)">
                                <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'
                                    }}</v-icon>
                            </v-btn>
                        </td>
                        <td class="col-md-1">
                            {{categories[item.category]}}
                        </td>
                        <td class="col-md-3">
                            {{ getMomentDate(item.created) }}
                        </td>
                        <td class="col-md-2">
                            <a :href="`/pharmacists/${item?.creator?._id}`" target="_blank">
                                {{item?.creator?.firstname}}
                            </a>
                        </td>
                        <td class="col-md-2">
                            {{item?.resubmit}}
                        </td>
                        <td class="col-md-2">
                            <a :href="`/crm/${item?._id}/edit`" target="_blank">
                                <v-icon color="primary">
                                    mdi-pencil
                                </v-icon>
                            </a>
                        </td>
                    </tr>
                </template>

                <template v-slot:expanded-item="{ item }">
                    <tr :data-id="item?._id">
                        <td colspan="3"></td>
                        <td colspan="6">
                            <div class="px-4 py-4">
                                <strong>Beschreibung:</strong>
                                <div class="description">{{ item.description }}</div>
                            </div>
                        </td>
                        <td colspan="1"></td>
                    </tr>
                </template>
            </v-data-table>
        </v-card-text>
    </v-card>

</div>
</template>

<script>
import axios from "axios";
import moment from 'moment';
import { mapMutations } from "vuex";
import { ticketFilters } from "../../constants";

export default {
    props: {
        pharmacy_id: String,
        appointment_id: String
    },
    data: () => ({
        filter: {
            title: "",
            wf_state: "",
            filter_type: "contains"
        },
        expanded: [],
        ticketsList: [],
        ticketsCount: 0,
        workflow_states: {},
        categories: {},
        pageType: "",
        pageNumber: 1,
        tableOptions: {},
        isLoading: false,
        statusOptions: ticketFilters.statusOptions,
        headers: ticketFilters.headers
    }),
    methods: {
        onFilterChanged() {
            this.filter.filter_type = this.filter.filter_type === "contains" ? "not" : "contains";
        },
        async fetchTickets(page = 1, pageSize = 10) {
            this.isLoading = true;
            try {
                const payload = {
                    filter: this.filter,
                    page: page,
                    page_size: pageSize,
                    page_type: this.pageType
                }
                const response = await axios.post(`/api/tickets`, payload);

                this.ticketsList = response.data.tickets;
                this.ticketsCount = response.data.count;
                this.workflow_states = response.data.workflow_states;
                this.categories = response.data.categories;
                this.tableOptions.page = page;
                this.tableOptions.itemsPerPage = pageSize;
            } catch (error) {
                this.alertError();
            } finally {
                this.isLoading = false;
            }
        },
        async onOptionsChange({ page, itemsPerPage }) {
            await this.fetchTickets(page, itemsPerPage);
        },

        filterTickets: _.debounce(async function () {
            this.fetchTickets()
        }, 200),

        async clearFilter() {
            this.filter = {
                ...this.filter,
                title: "",
                wf_state: "",
                filter_type: "contains",
            }
            await this.fetchTickets();
        },
        getMomentDate(created) {
            return moment.utc(created).local().format('DD-MM-yyyy HH:mm')
        },
        ticketAppointmentHref(item) {
            const pharmacyId = item?.ref_pharmacy?._id;
            const appointmentId = item?.ref_appointment_id || item?.ref_appointment?._id;
            if (!pharmacyId || !appointmentId) {
                return "";
            }
            return `/pharmacies/${pharmacyId}/${appointmentId}/tickets`;
        },
        formatAppointmentDate(dateStr) {
            if (!dateStr) return '';
            const date = new Date(dateStr);
            return date.toLocaleDateString('de-DE');
        },
        ...mapMutations({
            alertError: 'alert/error',
        })
    },
    computed: {
        isFiltered() {
            return this.filter.title || this.filter.wf_state
        }
    },
    watch: {
        filter: {
            handler() {
                this.filterTickets()
            },
            deep: true
        },
    },
    async created() {
        const type = new URLSearchParams(window.location.search).get('type');
        this.pageType = type || "";
        this.filter.appointment_id =  this.appointment_id || '';
        this.filter.pharmacy_id = this.pharmacy_id;
    }
};
</script>

<style scoped>

.filter-container {
    border: 1px solid #EEE;
    background: #FFFFFF;
    padding: 1rem;
    border-radius: 8px;
    margin-bottom: 8px;
    align-items: center;
}

th {
    vertical-align: middle;
    padding: 0 10px !important;
    border-bottom: none !important;
    font-size: 15px !important;
    color: black !important;
}

.description {
  white-space: pre-line;
}

</style>
